import { rtkOrgClient } from '@gm-commercial/rtk-clients';
import { useSelector } from 'react-redux';

import { isFleetProgramsAvailable } from '~/common/utils/fleet-programs.utils';
import { getActingOrgId } from '~/store/profile/actingProfile.utils';
import { selectActingProfile } from '~/store/profile/profileSlice.selectors';

export type FleetProgramsAvailability = {
  /** True while the organization record for the current org id is still loading. */
  isPending: boolean;
  /** True when the menu and page should be shown. */
  isAvailable: boolean;
};

/**
 * Whether Fleet programs should show for the organization in context.
 *
 * The country always comes from that organization's record. The menu stays
 * hidden and the page waits while the record for the current org id is loading.
 * A missing org id or a failed lookup hides the surface.
 * @returns Pending and availability flags for the menu and page
 */
const useFleetProgramsAvailability = (): FleetProgramsAvailability => {
  const actingProfile = useSelector(selectActingProfile);
  const organizationId = getActingOrgId(actingProfile);

  const { currentData, isError } = rtkOrgClient.useGetOrgByIdQuery(
    { id: organizationId ?? '' },
    { skip: !organizationId }
  );

  if (!organizationId) {
    return { isPending: false, isAvailable: false };
  }

  if (currentData) {
    const organization = currentData.result;
    return {
      isPending: false,
      isAvailable:
        !!organization &&
        currentData.success &&
        isFleetProgramsAvailable(organization.countryOfOperation),
    };
  }

  if (isError) {
    return { isPending: false, isAvailable: false };
  }

  return { isPending: true, isAvailable: false };
};

export default useFleetProgramsAvailability;
